/**
 * Nested T-Row Orchestrator — Interleaving/Zipper Pattern
 *
 * Renders nested T-hangar buildings where two rows of same-direction
 * T-units interleave their tail sections in a shared middle zone.
 *
 * The orchestrator handles ALL partition walls (wing-zone, step, tail-zone)
 * because the interleaving geometry is fundamentally different from standard
 * Z-shaped partitions. TShapedUnitComposer only renders front (door) and
 * back (tail end) walls per unit.
 *
 * Building zones (south to north, +Z to -Z):
 *   Row A wings  → Interleaving tails → Row B wings
 *   (wing_depth)    (tail_depth)         (wing_depth)
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { GeneratedBuilding, GeneratedUnit } from '@/lib/templates/types'
import { renderTUnit, type TUnitRenderOptions } from '../composers/TShapedUnitComposer'
import { renderFloor } from '../primitives/FloorRenderer'
import { createWallMaterial, createPartitionMaterial, applyShadowSettings } from '../utils/materials'
import type { EndSpaceMode } from '@/lib/templates/nested-t-hangar/params'

export interface NestedTRowRenderOptions extends TUnitRenderOptions {
  buildingId?: string
  buildingName?: string
  position?: [number, number, number]
  rotationDeg?: number
}

/**
 * Render a nested T-hangar building with interleaving wall geometry
 */
export function renderNestedTRowBuilding(
  building: GeneratedBuilding,
  units: GeneratedUnit[],
  options: NestedTRowRenderOptions = {}
): THREE.Group {
  const {
    buildingId,
    buildingName,
    position = [0, 0, 0],
    rotationDeg,
    ...unitOptions
  } = options

  const group = new THREE.Group()
  group.name = `building-${buildingId || building.code}`
  group.userData = {
    buildingId: buildingId || building.code,
    buildingName: buildingName || building.name,
    buildingType: building.type,
    template: 'nested_t_hangar',
    unitCount: units.length,
  }

  // ── Extract dimensions from type_config ─────────────────────────────
  const floorColor = building.settings?.floor_color || unitOptions.floorColor
  const wallColor = building.settings?.wall_color || unitOptions.wallColor
  const wallThk = (building.type_config?.wall_thickness_ft as number) ?? 0.5
  const unitWidth = (building.type_config?.unit_width_ft as number) ?? 44.5
  const tailWidth = (building.type_config?.tail_width_ft as number) ?? 22.5
  const wingDepth = (building.type_config?.wing_depth_ft as number) ?? 21
  const tailDepth = (building.type_config?.tail_depth_ft as number) ?? wingDepth
  const buildingHeight = building.exterior_height
  const buildingWidth = building.exterior_width
  const buildingDepth = building.exterior_depth
  const halfThk = wallThk / 2

  // Derived z-coordinates for zone boundaries (tailDepth-aware)
  const halfUD = (wingDepth + tailDepth) / 2
  const stepA = (tailDepth - wingDepth) / 2           // Row A step line (=0 when tailDepth=wingDepth)
  const stepB = -(wingDepth + tailDepth) / 2           // Row B step line (=-wingDepth when tailDepth=wingDepth)
  const wingCenterA = tailDepth / 2                    // Row A wing partition center
  const wingCenterB = -(wingDepth + tailDepth + wingDepth) / 2  // Row B wing partition center
  const tailCenter = -wingDepth / 2                    // Tail zone center (always same)
  const northFaceZ = -wingDepth - halfUD - halfThk     // North exterior wall line

  const unitsGroup = new THREE.Group()
  unitsGroup.name = 'units'

  // ── Materials ───────────────────────────────────────────────────────
  const extMat = createWallMaterial({ color: wallColor ?? undefined })
  const partMat = createPartitionMaterial({ color: wallColor ?? undefined })

  // ── Unified building floor ──────────────────────────────────────────
  const buildingFloor = renderFloor(
    { width: buildingWidth, depth: buildingDepth },
    { color: floorColor ?? undefined, enableShadows: true }
  )
  buildingFloor.position.x = (buildingWidth / 2) * FEET_TO_METERS
  buildingFloor.name = 'building-floor'
  unitsGroup.add(buildingFloor)

  // ── Read per-side end-space modes from type_config ──────────────────
  // Support both new per-side and legacy single mode
  const legacyMode = (building.type_config?.end_space_mode as EndSpaceMode) || 'separate'
  const leftEndMode: EndSpaceMode = (building.type_config?.left_end_mode as EndSpaceMode) || legacyMode
  const rightEndMode: EndSpaceMode = (building.type_config?.right_end_mode as EndSpaceMode) || legacyMode

  // ── Split units into Row A and Row B (END units are data-only, no rendering) ──
  const rowAUnits = units.filter(u => u.code.includes('-A-'))
  const rowBUnits = units.filter(u => u.code.includes('-B-'))
  const rowACount = rowAUnits.length
  const rowBCount = rowBUnits.length

  // ── Suppress ALL back edges for nested units ────────────────────────
  // Orchestrator renders closing walls at the exact step Z positions
  // to avoid double walls (unit back walls sit at stepA±halfThk offset)
  const tUnits = [...rowAUnits, ...rowBUnits]
  for (const unit of tUnits) {
    const backEdge = unit.tHangarEdges?.find(e => e.id === 'back')
    if (backEdge) backEdge.type = 'internal'
  }

  // ── Render individual T-units (front walls only) ──────────────────
  for (let i = 0; i < tUnits.length; i++) {
    const unitGroup = renderTUnit(tUnits[i], i, {
      ...unitOptions,
      floorColor,
      wallColor,
      wallThickness: wallThk,
      skipFloor: true,
    })
    unitsGroup.add(unitGroup)
  }

  // ── Helper: create a wall box and position it ───────────────────────
  const addWall = (
    name: string,
    w: number, h: number, d: number,
    x: number, y: number, z: number,
    material: THREE.Material
  ) => {
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(w * FEET_TO_METERS, h * FEET_TO_METERS, d * FEET_TO_METERS),
      material
    )
    mesh.position.set(x * FEET_TO_METERS, y * FEET_TO_METERS, z * FEET_TO_METERS)
    mesh.name = name
    applyShadowSettings(mesh, true, true)
    unitsGroup.add(mesh)
  }

  // ── Coordinate reference ────────────────────────────────────────────
  // Unit centers:
  //   Row A center at z=0 (offset_y=0)
  //   Row B center at z=-wingDepth (offset_y=wingDepth)
  //
  // Zone boundaries (tailDepth-aware):
  //   z = +halfUD+halfThk    : south exterior wall line
  //   z = +halfUD            : Row A front (door side)
  //   z = stepA              : Row A step line (= (tailDepth-wingDepth)/2)
  //   z = tailCenter         : Tail zone center (= -wingDepth/2, always)
  //   z = stepB              : Row B step line (= -(wingDepth+tailDepth)/2)
  //   z = northFaceZ         : north exterior wall line

  const hCenter = buildingHeight / 2

  // ── Row A wing-zone partition walls (N-1) ───────────────────────────
  // Straight vertical walls between adjacent Row A wing sections.
  // These run from stepA to stepA+wingDepth (front), centered at wingCenterA
  for (let i = 0; i < rowACount - 1; i++) {
    const wallX = rowAUnits[i].offset_x + unitWidth + halfThk
    addWall(
      `partition-rowA-wing-${i}`,
      wallThk, buildingHeight, wingDepth,
      wallX, hCenter, wingCenterA,
      partMat
    )
  }

  // ── Row B wing-zone partition walls (N-2) ───────────────────────────
  // Between adjacent Row B wing sections.
  // z range: stepB to stepB-wingDepth (front), centered at wingCenterB
  for (let j = 0; j < rowBCount - 1; j++) {
    const wallX = rowBUnits[j].offset_x + unitWidth + halfThk
    addWall(
      `partition-rowB-wing-${j}`,
      wallThk, buildingHeight, wingDepth,
      wallX, hCenter, wingCenterB,
      partMat
    )
  }

  // ── Row A step walls ────────────────────────────────────────────────
  // Horizontal walls at z=stepA (Row A step line), connecting wing wall to tail boundary.
  // Each inter-unit gap has two step walls (one from each adjacent unit's step).
  for (let i = 0; i < rowACount - 1; i++) {
    const unitRightWingX = rowAUnits[i].offset_x + unitWidth + halfThk
    const unitRightTailX = rowAUnits[i].offset_x + unitWidth / 2 + tailWidth / 2 + halfThk

    // Right step of unit i: from wing wall X to tail wall X
    const stepLen = unitRightWingX - unitRightTailX
    if (stepLen > 0.01) {
      addWall(
        `step-rowA-right-${i}`,
        stepLen, buildingHeight, wallThk,
        unitRightTailX + stepLen / 2, hCenter, stepA,
        partMat
      )
    }

    // Left step of unit i+1: from wing wall X to tail wall X
    const nextUnitLeftWingX = rowAUnits[i + 1].offset_x - halfThk
    const nextUnitLeftTailX = rowAUnits[i + 1].offset_x + unitWidth / 2 - tailWidth / 2 - halfThk

    const stepLenLeft = nextUnitLeftTailX - nextUnitLeftWingX
    if (stepLenLeft > 0.01) {
      addWall(
        `step-rowA-left-${i + 1}`,
        stepLenLeft, buildingHeight, wallThk,
        nextUnitLeftWingX + stepLenLeft / 2, hCenter, stepA,
        partMat
      )
    }
  }

  // ── Row B step walls ────────────────────────────────────────────────
  // At z=stepB (Row B step line)
  for (let j = 0; j < rowBCount - 1; j++) {
    const unitRightWingX = rowBUnits[j].offset_x + unitWidth + halfThk
    const unitRightTailX = rowBUnits[j].offset_x + unitWidth / 2 + tailWidth / 2 + halfThk

    const stepLen = unitRightWingX - unitRightTailX
    if (stepLen > 0.01) {
      addWall(
        `step-rowB-right-${j}`,
        stepLen, buildingHeight, wallThk,
        unitRightTailX + stepLen / 2, hCenter, stepB,
        partMat
      )
    }

    const nextUnitLeftWingX = rowBUnits[j + 1].offset_x - halfThk
    const nextUnitLeftTailX = rowBUnits[j + 1].offset_x + unitWidth / 2 - tailWidth / 2 - halfThk

    const stepLenLeft = nextUnitLeftTailX - nextUnitLeftWingX
    if (stepLenLeft > 0.01) {
      addWall(
        `step-rowB-left-${j + 1}`,
        stepLenLeft, buildingHeight, wallThk,
        nextUnitLeftWingX + stepLenLeft / 2, hCenter, stepB,
        partMat
      )
    }
  }

  // ── Tail-end closing walls (flush with step lines) ─────────────────
  // Replace per-unit back walls with orchestrator walls at exact step Z,
  // eliminating the halfThk offset that caused visible double walls.

  // Row A tail-end closing walls at Z = stepB (flush with Row B step line)
  for (let i = 0; i < rowACount; i++) {
    // Skip merge_row_a edge units — L-shape opening stays open
    if (i === 0 && leftEndMode === 'merge_row_a') continue
    if (i === rowACount - 1 && rightEndMode === 'merge_row_a') continue

    const tailCenterX = rowAUnits[i].offset_x + unitWidth / 2
    addWall(
      `closing-rowA-back-${i}`,
      tailWidth, buildingHeight, wallThk,
      tailCenterX, hCenter, stepB,
      partMat
    )
  }

  // Row B tail-end closing walls at Z = stepA (flush with Row A step line)
  for (let j = 0; j < rowBCount; j++) {
    const tailCenterX = rowBUnits[j].offset_x + unitWidth / 2
    addWall(
      `closing-rowB-back-${j}`,
      tailWidth, buildingHeight, wallThk,
      tailCenterX, hCenter, stepA,
      partMat
    )
  }

  // ── Tail-zone interleaving walls ────────────────────────────────────
  // In the shared tail zone z=[stepA, stepB], walls between Row A and Row B tails.
  // Row A tail right edge: offset_x + unitWidth/2 + tailWidth/2
  // Row B tail left edge: offset_x + unitWidth/2 - tailWidth/2
  // Wall between them runs the full tailDepth, centered at tailCenter.

  // For each Row A unit i, add right-tail wall (separating from Row B unit to the right)
  for (let i = 0; i < rowACount; i++) {
    const aRightTailEdge = rowAUnits[i].offset_x + unitWidth / 2 + tailWidth / 2

    // Check if there's a Row B unit to the right
    if (i < rowBCount) {
      const bLeftTailEdge = rowBUnits[i].offset_x + unitWidth / 2 - tailWidth / 2
      // Wall between Row A tail right and Row B tail left
      const wallX = (aRightTailEdge + bLeftTailEdge) / 2
      addWall(
        `interleave-A${i}-B${i}`,
        wallThk, buildingHeight, tailDepth,
        wallX, hCenter, tailCenter,
        partMat
      )
    }
  }

  // For each Row B unit j, add right-tail wall (separating from Row A unit j+1 to the right)
  for (let j = 0; j < rowBCount; j++) {
    const bRightTailEdge = rowBUnits[j].offset_x + unitWidth / 2 + tailWidth / 2

    if (j + 1 < rowACount) {
      const aLeftTailEdge = rowAUnits[j + 1].offset_x + unitWidth / 2 - tailWidth / 2
      const wallX = (bRightTailEdge + aLeftTailEdge) / 2
      addWall(
        `interleave-B${j}-A${j + 1}`,
        wallThk, buildingHeight, tailDepth,
        wallX, hCenter, tailCenter,
        partMat
      )
    }
  }

  // ── End-cap walls ───────────────────────────────────────────────────
  // Left end-cap (full building depth)
  addWall('endcap-left', wallThk, buildingHeight, buildingDepth,
    halfThk, hCenter, -(buildingDepth / 2 - halfUD - halfThk), extMat)

  // Right end-cap
  addWall('endcap-right', wallThk, buildingHeight, buildingDepth,
    buildingWidth - halfThk, hCenter, -(buildingDepth / 2 - halfUD - halfThk), extMat)

  // ── Edge exterior walls for Row A first/last tail sides ─────────────
  // Row A first unit left tail wall (exterior, runs through tail zone)
  if (rowACount > 0) {
    // LEFT side: skip A-01 exterior tail wall + step wall if merge_row_a
    // (opens A's tail zone to the step area below for L-shaped extension)
    if (leftEndMode !== 'merge_row_a') {
      const firstA = rowAUnits[0]
      const leftTailX = firstA.offset_x + unitWidth / 2 - tailWidth / 2 - halfThk
      addWall('exterior-rowA-first-left-tail', wallThk, buildingHeight, tailDepth,
        leftTailX, hCenter, tailCenter, extMat)

      // Row A first unit left step (z=stepA)
      const leftWingX = firstA.offset_x - halfThk
      const stepLen = leftTailX - leftWingX
      if (stepLen > 0.01) {
        addWall('step-rowA-first-left', stepLen, buildingHeight, wallThk,
          leftWingX + stepLen / 2, hCenter, stepA, extMat)
      }
    }

    // RIGHT side: skip A-last exterior tail wall + step wall if merge_row_a
    if (rightEndMode !== 'merge_row_a') {
      const lastA = rowAUnits[rowACount - 1]
      const rightTailX = lastA.offset_x + unitWidth / 2 + tailWidth / 2 + halfThk
      addWall('exterior-rowA-last-right-tail', wallThk, buildingHeight, tailDepth,
        rightTailX, hCenter, tailCenter, extMat)

      // Row A last unit right step (z=stepA)
      const rightWingX = lastA.offset_x + unitWidth + halfThk
      const stepLenR = rightWingX - rightTailX
      if (stepLenR > 0.01) {
        addWall('step-rowA-last-right', stepLenR, buildingHeight, wallThk,
          rightTailX + stepLenR / 2, hCenter, stepA, extMat)
      }
    }
  }

  // ── Edge exterior walls for Row B first/last tail sides ─────────────
  if (rowBCount > 0) {
    // LEFT side: skip B-01 exterior tail wall + step wall if merge_row_b
    // (opens B's tail zone to the step area above for L-shaped extension)
    if (leftEndMode !== 'merge_row_b') {
      const firstB = rowBUnits[0]
      const leftTailX = firstB.offset_x + unitWidth / 2 - tailWidth / 2 - halfThk
      addWall('exterior-rowB-first-left-tail', wallThk, buildingHeight, tailDepth,
        leftTailX, hCenter, tailCenter, extMat)

      // Row B first unit left step (z=stepB)
      const leftWingX = firstB.offset_x - halfThk
      const stepLen = leftTailX - leftWingX
      if (stepLen > 0.01) {
        addWall('step-rowB-first-left', stepLen, buildingHeight, wallThk,
          leftWingX + stepLen / 2, hCenter, stepB, extMat)
      }
    }

    // RIGHT side: skip B-last exterior tail wall + step wall if merge_row_b
    if (rightEndMode !== 'merge_row_b') {
      const lastB = rowBUnits[rowBCount - 1]
      const rightTailX = lastB.offset_x + unitWidth / 2 + tailWidth / 2 + halfThk
      addWall('exterior-rowB-last-right-tail', wallThk, buildingHeight, tailDepth,
        rightTailX, hCenter, tailCenter, extMat)

      // Row B last unit right step (z=stepB)
      const rightWingX = lastB.offset_x + unitWidth + halfThk
      const stepLenR = rightWingX - rightTailX
      if (stepLenR > 0.01) {
        addWall('step-rowB-last-right', stepLenR, buildingHeight, wallThk,
          rightTailX + stepLenR / 2, hCenter, stepB, extMat)
      }
    }
  }

  // ── Wing-zone end walls (per-side logic) ───────────────────────────
  // Row B starts at pitch/2 from left, leaving gaps at both ends of the
  // Row B wing zone. Each side is independently controlled.

  if (rowBCount > 0) {
    const bFirstLeftWing = rowBUnits[0].offset_x - halfThk
    const leftGapWidth = bFirstLeftWing - wallThk
    const bLastRightWing = rowBUnits[rowBCount - 1].offset_x + unitWidth + halfThk
    const rightGapWidth = buildingWidth - wallThk - bLastRightWing

    // ── North-face walls for end-space gaps (all modes) ─────────────
    // Close the gap's north face (Row B door side) at z=northFaceZ
    const leftNorthWidth = rowBUnits[0].offset_x - wallThk
    if (leftNorthWidth > 0.01) {
      addWall('endspace-left-north', leftNorthWidth, buildingHeight, wallThk,
        wallThk + leftNorthWidth / 2, hCenter, northFaceZ, extMat)
    }
    const rightNorthWidth = buildingWidth - wallThk - (rowBUnits[rowBCount - 1].offset_x + unitWidth)
    if (rightNorthWidth > 0.01) {
      addWall('endspace-right-north', rightNorthWidth, buildingHeight, wallThk,
        rowBUnits[rowBCount - 1].offset_x + unitWidth + rightNorthWidth / 2, hCenter, northFaceZ, extMat)
    }

    // ── LEFT SIDE ──
    if (leftGapWidth > 0.01) {
      if (leftEndMode === 'separate') {
        // Closing wall (partition) between end space and B-01 wing
        addWall('rowB-wing-left-close', wallThk, buildingHeight, wingDepth,
          bFirstLeftWing, hCenter, wingCenterB, partMat)
        // No step wall — L-shaped end space connects tail zone and wing zone
      } else if (leftEndMode === 'merge_row_a') {
        // Closing wall (exterior) keeps gap walled off from B
        addWall('rowB-wing-left-close', wallThk, buildingHeight, wingDepth,
          bFirstLeftWing, hCenter, wingCenterB, extMat)
        // No step wall — L-shape open to tail zone
      } else {
        // merge_row_b: B-01 extends into gap — no closing wall, no step wall
        // Wing zone connects upward to tail zone step area (L-shaped extension)
      }
    }

    // ── RIGHT SIDE ──
    if (rightGapWidth > 0.01) {
      if (rightEndMode === 'separate') {
        // Closing wall (partition) between end space and B-last wing
        addWall('rowB-wing-right-close', wallThk, buildingHeight, wingDepth,
          bLastRightWing, hCenter, wingCenterB, partMat)
        // No step wall — L-shaped end space connects tail zone and wing zone
      } else if (rightEndMode === 'merge_row_a') {
        // Closing wall (exterior) keeps gap walled off from B
        addWall('rowB-wing-right-close', wallThk, buildingHeight, wingDepth,
          bLastRightWing, hCenter, wingCenterB, extMat)
        // No step wall — L-shape open to tail zone
      } else {
        // merge_row_b: B-last extends into gap — no closing wall, no step wall
        // Wing zone connects upward to tail zone step area (L-shaped extension)
      }
    }
  }

  // ── Center the building ─────────────────────────────────────────────
  // South edge at z=+(halfUD+wallThk), north edge at z=-(wingDepth+halfUD+wallThk)
  // Geometric center Z = (halfUD - wingDepth - halfUD) / 2 = -wingDepth/2
  // So we shift by +wingDepth/2 to center (same regardless of tailDepth)
  const buildingCenterX = buildingWidth / 2
  const buildingCenterZ = wingDepth / 2

  // Move the floor to correct position (it was placed at buildingWidth/2, 0)
  // Need to adjust floor Z position to match building center
  buildingFloor.position.z = -buildingCenterZ * FEET_TO_METERS

  unitsGroup.position.x = -buildingCenterX * FEET_TO_METERS
  unitsGroup.position.z = buildingCenterZ * FEET_TO_METERS

  group.add(unitsGroup)

  // Apply building rotation
  const rotation = rotationDeg ?? (building.rotation * 180 / Math.PI)
  if (rotation !== 0) {
    group.rotation.y = (rotation * Math.PI) / 180
  }

  // Apply building position
  group.position.set(
    position[0] * FEET_TO_METERS,
    position[1] * FEET_TO_METERS,
    position[2] * FEET_TO_METERS
  )

  return group
}
